import * as React from 'react'
import * as XLSX from 'xlsx'

// import html2canvas from 'html2canvas'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Slider from '@mui/material/Slider'

import Download from 'mdi-material-ui/Download'

import Histograms from './children/quality-control-children/Histogram'

import Pies from './children/quality-control-children/Pie'
import GroupHistogram from './children/quality-control-children/GroupHistogram'

import Areas from './children/quality-control-children/Area'
import VariationDensity from './children/quality-control-children/VariationDensity'
import Lines from './children/quality-control-children/Line'
import {
  Card,
  CardContent,
  IconButton,
  Popover,
  Stack,
  styled,
  Tab,
  Tabs,
  TextField,
  Tooltip
} from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import { Area, Column, Histogram, Scatter } from '@antv/g2plot'
import { useMutation, useQuery } from 'react-query'
import { useRouter } from 'next/router'
import {
  fetchProjectsByParams,
  getAlleleFrequencyQC,
  getGlsQc,
  getQCData,
  getReadDepth,
  GlsQc,
  ISamplesQcData,
  Project
} from '@/@core/services/app.service'
import axios from 'axios'
import { NObject } from '@/@core/components/filter-variants'
import HistogramFq from './children/quality-control-children/HistogramFq'
import Scatters from './children/quality-control-children/Scatter'
import AntdTables, { ColumnTypes } from '@/@core/components/tables-antd'

import { Badge, Descriptions } from 'antd'
import type { DescriptionsProps } from 'antd'
import { HelpCircleOutline, OpenInNew, Read } from 'mdi-material-ui'
import { jsonToCSV } from 'react-papaparse'
import {
  calculateAndFormat,
  formatDate,
  formatNumberWithCommas,
  isWindow
} from '@/@core/utils/utils'
import i18n from '@/configs/i18n'
import { DataGrid } from '@mui/x-data-grid'
import QualityControlFq2 from './QualityControlFq2'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TypographyEllipsis = styled(Typography)(() => ({
  width: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: '0.875rem'
}))

const marks = [
  {
    value: 0,
    label: '0%'
  },
  {
    value: 100,
    label: '100%'
  }
]

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number | string) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

function createData(
  value: string,
  CoverageUniformity: string,
  Interpretation: string
) {
  return { value, CoverageUniformity, Interpretation }
}

const rows = [
  createData('1.0-1.2', 'Excellent', 'Almost all bases have similar coverage'),
  createData('1.2-1.5', 'Very Good', 'Slight variation, but very usable'),
  createData('1.5-2.0', 'Moderate/Acceptable', 'Common for capture-based data'),
  createData('>2.0', 'Poor', 'Uneven coverage-may miss low-coverage bases')
]

function valuetext(value: number) {
  return `${value}%`
}

const QualityControl = React.forwardRef(
  (
    props: {
      analysisInfo: any
      isSample?: boolean
    },
    ref
  ) => {
    const { t } = useTranslation()
    const router = useRouter()

    const { id } = router.query
    const his1 = React.useRef<{
      plot: {
        current: Histogram
      }
    }>()
    const his2 = React.useRef<{
      plot: {
        current: Histogram
      }
    }>()
    const scatter = React.useRef<{
      plot: {
        current: Scatter
      }
    }>()
    const [ratio, setRatio] = React.useState(3)
    const [tabValue, setTabValue] = React.useState(0)
    const [hisData2, setHisData2] = React.useState([])
    const [tsvData, setTsvData] = React.useState([])
    const [hisData, setHisData] = React.useState([])
    const [samples, setSamples] = React.useState<any[]>([])
    const [fastqc, setFastqc] = React.useState<any>({
      metricsData: null,
      barChartData: null,
      metricsReports: [],
      scatterUrl: 'string'
    })
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const [timer, setTimer] = React.useState<NodeJS.Timer>()
    const open = Boolean(anchorEl)

    const openId = open ? 'simple-popover' : undefined

    const handlePopoverOpen = (event: any) => {
      if (open) {
        handlePopoverClose()

        return
      }

      setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
      setAnchorEl(null)
    }

    const hisConfig2 = {
      xField: 'name',
      yField: 'value',
      data: hisData2,
      label: {
        // 配置样式
        style: {
          fill: '#FFFFFF',
          opacity: 0.6
        },
        position: 'middle'
      }
    }

    const hisConfig = {
      title: {
        visible: true,
        text: t('TS/TV ratio'),
        style: {
          textAlign: 'center'
        }
      },
      binField: 'value',
      yAxis: {
        line: {
          visible: true
        },
        title: {
          text: t('%Coverage rate'),
          visible: true
        }
      },
      xAxis: {
        line: {
          visible: true
        },
        title: {
          text: t('Reads depth'),
          visible: true
        }
      }
    }

    const [value, setValue] = React.useState([0, 100])
    const [slider, setSlider] = React.useState(false)
    const [qc, setQc] = React.useState({
      genomicCount: 0,
      exonicCount: 0,
      avgReadDepth: 0,
      avgExonReadDepth: 0,
      avgIntronReadDepth: 0,
      intronicCount: 0
    })
    const [GlsQc, setGlsQc] = React.useState<GlsQc | null>()

    const [megabase, setMegabase] = React.useState({
      megabasePercentage: '',
      totalMegabase: '',
      imageUrl: ''
    })

    const handleChangeValue = (event: Event, newValue: number | number[]) => {
      setValue(newValue as number[])

      // 过滤数据
    }

    const { data: projects, refetch } = useQuery<Project[]>(
      'data',
      async () =>
        await fetchProjectsByParams({
          id: props.analysisInfo.projectId
        } as any),
      {
        keepPreviousData: true
      }
    )

    const getHomRegions = useMutation({
      mutationFn: async (id: string) => await getQCData(id),
      onSuccess: async (data: ISamplesQcData, params) => {
        //post返回的结果
        // const datas = {
        //   roh: {
        //     imageUrl:
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/vcf_results/HomRegions.jpg?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768359682&Signature=RKIuNiXG38PE1pZMiYNYZX04XVc%3D',
        //     totalMegabase: '111.43Mb',
        //     megabasePercentage: '3.87%'
        //   },
        //   fastqc: {
        //     metricsData: {
        //       intron20X: 5.5,
        //       mt20X: 100,
        //       seqtk_q30: [
        //         {
        //           R1_Q30: '3.6',
        //           R2_Q30: '4.3',
        //           average_Q30: '3.95',
        //           R1_name: '',
        //           R2_name: ''
        //         }
        //       ],
        //       rawReads: '131,828,118',
        //       readsMapped: '131,799,966',
        //       readsMappedPercent: '100%',
        //       mismatchRate: '0.002353',
        //       readsAfterDeduped: '123,951,314',
        //       readsAfterDedupedPercent: '6%',
        //       pcrDuplicateRate: '6%',
        //       readsOnTargetRegionsPercent: '85.5%',
        //       readsMappedOnTargetRegion: '105,525,900',
        //       intronMeanDepth: '7.04',
        //       mtMeanDepth: '22066.14',
        //       targetRegionLength: '137,996,298',
        //       targetMeanDepth: '91.31',
        //       meanCoverage: '6.25',
        //       targetRegionCoverage: '56',
        //       targetRegionAverageDepths: [
        //         { name: '4x', value: 48.9 },
        //         { name: '10x', value: 46.3 },
        //         { name: '20x', value: 43.8 },
        //         { name: '30x', value: 42.1 }
        //       ]
        //     },
        //     barChartData: [
        //       { name: '1x', value: 56 },
        //       { name: '10x', value: 46.3 },
        //       { name: '20x', value: 43.8 },
        //       { name: '30x', value: 42.1 },
        //       { name: '40x', value: 40.6 },
        //       { name: '50x', value: 39.3 }
        //     ],
        //     metricsReports: [
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/fastq_results/metrics/metrics-chart-0.jpg?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768359682&Signature=31Av58EU6LX5GvYJN4vO2mMbm8E%3D',
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/fastq_results/metrics/metrics-chart-1.jpg?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768359682&Signature=GSr5Q42jo6spkmDIa90EnWaAq4c%3D',
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/fastq_results/metrics/metrics-chart-2.jpg?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768359682&Signature=cJMDFvmUPQ3Fc7rsBMRAPvciZMg%3D',
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/fastq_results/metrics/metrics-chart-3.jpg?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768359682&Signature=jUimlib%2B7FwIXMtIkVDBv2DguH4%3D'
        //     ],
        //     scatterUrl:
        //       'https://glsofort.oss-cn-shenzhen.aliyuncs.com/samples/5033/fastq_results/scatter_data.tsv?AWSAccessKeyId=LTAI5tDQz9RP8SQ7tMC2Peob&Expires=1768378682&Signature=7hsanxfZ1qy6QzWiXtNueUTsTe4%3D'
        //   },
        //   tstv: { ts: '175138', tv: '80114', ratio: '2.18611' }
        // }

        // data.fastqc = datas.fastqc as any

        //

        setMegabase({
          megabasePercentage: data.roh.megabasePercentage,
          totalMegabase: data.roh.totalMegabase,
          imageUrl: data.roh.imageUrl
        })

        setFastqc({
          metricsData: data.fastqc?.metricsData,
          barChartData: data.fastqc?.barChartData,
          metricsReports: data.fastqc?.metricsReports,
          scatterUrl: data.fastqc?.scatterUrl
        })

        //

        his2.current?.plot.current.changeData(
          data.fastqc.barChartData
            ? data.fastqc.barChartData.map((item) => {
                return {
                  ...item,
                  value: item.value
                }
              })
            : []
        )

        if (data.fastqc?.scatterUrl) {
          const { data: text } = await axios.get(data.fastqc.scatterUrl)

          if (text) {
            // 解析TSV文件
            const rows = text.trim().split('\n')
            const headers = rows[0].split('\t') as string[]

            const tsvData = rows
              .slice(1)
              .map((row: string) => {
                const values = row.split('\t')
                const obj: NObject = {}

                headers.forEach((header, index) => {
                  obj[header] = values[index]
                })

                return obj
              })
              .filter((i: any) => i && i.chrom)

            setTsvData(tsvData)

            //

            // scatter.current?.plot.current.changeData(
            //   tsvData
            //     ? tsvData.map((item: any) => {
            //         return {
            //           ...item,
            //           chrom: `${item.chrom}`,
            //           tavg: Number(item.tavg)
            //         }
            //       })
            //     : []
            // )
          }
        }
      }
    })

    const fetchAlleleFrequencyQC = useMutation({
      mutationFn: async (data: { start: number; end: number; id: any }) =>
        await getAlleleFrequencyQC(data.id, {
          start: data.start,
          end: data.end
        }),
      onSuccess: (data, params) => {
        //post返回的结果
        // refetch()
        //

        if (data && data.exonicCount && data.genomicCount) {
          setQc({
            genomicCount: data.genomicCount,
            exonicCount: data.exonicCount,
            intronicCount: data.intronicCount,
            avgReadDepth: props.analysisInfo.avgReadDepth ?? 0,
            avgExonReadDepth: props.analysisInfo.avgExonReadDepth ?? 0,
            avgIntronReadDepth: props.analysisInfo.avgIntronReadDepth ?? 0
          })
        }
      }
    })

    const fetchReadDepth = useMutation({
      mutationFn: async (data: { start: number; end: number; id: any }) =>
        await getReadDepth(data.id, {
          start: data.start,
          end: data.end
        }),
      onSuccess: (data) => {
        //post返回的结果
        // refetch()
        //

        if (
          data &&
          data.allReadDepthData &&
          Array.isArray(data.allReadDepthData)
        ) {
          his1.current?.plot.current.changeData(
            data.allReadDepthData.map((item: any) => {
              return {
                ...item,
                xTick: ` ${item.xTick} `
              }
            })
          )
        }
      }
    })

    const fetchGlsQc = useMutation({
      mutationFn: async (data: { id: any }) =>
        await getGlsQc({
          id: data.id,
          platform: 'parabricks'
        }),
      onSuccess: async (data: GlsQc, params) => {
        //post返回的结果
        // refetch()
        //

        if (data && data.sample_id) {
          setGlsQc(data)

          setFastqc({
            ...fastqc,
            metricsReports: [
              data.plot_urls.gcbias,
              data.plot_urls.insert_size,
              data.plot_urls.qualityscore,
              data.plot_urls.mean_quality_by_cycle
            ]
          })

          setMegabase({
            megabasePercentage: `${data.roh?.megabase_percentage}`,
            totalMegabase: `${data.roh?.total_megabases}`,
            imageUrl: data.roh?.image_url
          })

          if (data.genome_scatter_url) {
            const { data: text } = await axios.get(data.genome_scatter_url)

            if (text) {
              // 解析TSV文件
              const rows = text.trim().split('\n').join('\r').split('\r')
              const headers = rows[0].split('\t') as string[]

              const tsvData = rows
                .slice(1)
                .map((row: string) => {
                  const values = row.split('\t')
                  const obj: NObject = {}

                  headers.forEach((header, index) => {
                    obj[header] = values[index]
                  })

                  return obj
                })
                .filter((i: any) => i && i.chrom)

              setTsvData(tsvData)

              //

              // scatter.current?.plot.current.changeData(
              //   tsvData
              //     ? tsvData.map((item: any) => {
              //         return {
              //           ...item,
              //           chrom: `${item.chrom}`,
              //           tavg: Number(item.tavg)
              //         }
              //       })
              //     : []
              // )
            }
          }

          if (props.analysisInfo.vcfType === 'wgs') {
            const barChartData = [
              { name: '1x', value: data.pct_wgs_1x },
              { name: '10x', value: data.pct_wgs_10x },
              { name: '20x', value: data.pct_wgs_20x },
              { name: '30x', value: data.pct_wgs_30x },
              { name: '40x', value: data.pct_wgs_40x },
              { name: '50x', value: data.pct_wgs_50x }
            ]

            his2.current?.plot.current.changeData(
              barChartData
                ? barChartData.map((item) => {
                    return {
                      ...item,
                      value: item.value
                    }
                  })
                : []
            )
          } else {
            const barChartData = [
              { name: '1x', value: data.pct_target_1x },
              { name: '2x', value: data.pct_target_2x },
              { name: '10x', value: data.pct_target_10x },
              { name: '20x', value: data.pct_target_20x },
              { name: '30x', value: data.pct_target_30x }
            ]

            his2.current?.plot.current.changeData(
              barChartData
                ? barChartData.map((item) => {
                    return {
                      ...item,
                      value: item.value
                    }
                  })
                : []
            )
          }
        }
      }
    })

    const q30Column = [
      {
        field: 'r1_name',
        headerName: t('R1_name'),
        width: 240
      },
      {
        field: 'r1_q30',
        headerName: t('R1_Q30'),
        width: 120,
        renderCell: (params: any) => {
          return params.value >= 85
            ? `${params.value}(${t('Qualified')})`
            : `${params.value}(${t('UnQualified')})`
        }
      },
      {
        field: 'r2_name',
        headerName: t('R2_name'),
        width: 240
      },
      {
        field: 'r2_q30',
        headerName: t('R2_Q30'),
        width: 120,
        renderCell: (params: any) => {
          return params.value >= 85
            ? `${params.value}(${t('Qualified')})`
            : `${params.value}(${t('UnQualified')})`
        }
      },
      {
        field: 'avg_q30',
        headerName: t('average_Q30'),
        width: 120
      }
    ]

    const data = React.useMemo(async () => {
      if (
        props.analysisInfo.samples &&
        Array.isArray(props.analysisInfo.samples)
      ) {
        // 家系
        const data = await Promise.all(
          props.analysisInfo.samples.map(async (item: any) => {
            if (item.pipelineVersion) {
              //

              const readDepth = await fetchReadDepth.mutateAsync({
                start: value[0],
                end: value[1],
                id: item.id
              })

              const glsQc = await fetchGlsQc.mutateAsync({
                id: item.id
              })

              setQc({
                genomicCount: 0,
                exonicCount: 0,
                intronicCount: 0,
                avgReadDepth: props.analysisInfo.avgReadDepth ?? 0,
                avgExonReadDepth: props.analysisInfo.avgExonReadDepth ?? 0,
                avgIntronReadDepth: props.analysisInfo.avgIntronReadDepth ?? 0
              })
              return {
                ...item,
                ...glsQc,
                ...readDepth
              }
            } else {
              const alleleFrequencyQC =
                await fetchAlleleFrequencyQC.mutateAsync({
                  start: value[0],
                  end: value[1],
                  id: item.id
                })

              const readDepth = await fetchReadDepth.mutateAsync({
                start: value[0],
                end: value[1],
                id: item.id
              })

              const homRegions = await getHomRegions.mutateAsync(
                item.id as unknown as string
              )

              return {
                ...item,
                ...alleleFrequencyQC,
                ...readDepth,
                ...homRegions
              }
            }
          })
        )

        setSamples(data)
      } else {
        // 单样本

        if (props.analysisInfo.pipelineVersion) {
          //
          fetchGlsQc.mutateAsync({
            id: id
          })
          fetchReadDepth.mutateAsync({
            start: value[0],
            end: value[1],
            id: id
          })
        } else {
          fetchAlleleFrequencyQC.mutateAsync({
            start: value[0],
            end: value[1],
            id: id
          })

          fetchReadDepth.mutateAsync({
            start: value[0],
            end: value[1],
            id: id
          })

          getHomRegions.mutateAsync(id as unknown as string)
        }
      }

      return []
    }, [])

    const hisConfig1 = {
      ...hisConfig,
      title: {
        ...hisConfig.title,
        text: '变异深度'
      },
      xAxis: {
        ...hisConfig.xAxis,
        title: {
          text: null,
          visible: false
        }
      },
      yAxis: {
        ...hisConfig.yAxis,
        title: {
          text: t('%Coverage rate'),
          visible: true
        }
      },
      xField: 'xTick',
      yField: 'yValue',
      data: hisData
    }

    const items: any[] = React.useMemo(() => {
      if (samples && samples.length && samples.length === 1) {
        return samples.map((items: any) => {
          if (items.pipelineVersion) {
            const qcRules = projects?.find(
              (item: any) => item.id === items.projectId
            )?.qcRules
            const data = [
              'Total number of bases',
              'Raw reads',
              'filtered read count',
              'filtered base count',
              'read filtering retention ratio',
              'base filtering retention ratio',
              'aligned read count',
              'aligned base count',
              'read alignment ratio',
              'base alignment ratio',
              'targeted read count',
              'targeted base count',
              'read targeting ratio',
              'base targeting ratio'
            ]
            let TotalBasesQualified = true
            let RawReadsQualified = true
            let FilteredReadsQualified = true
            let FilteredBasesQualified = true
            let ReadFilteringRetentionRatioQualified = true
            let BaseFilteringRetentionRatioQualified = true
            let AlignedReadsQualified = true
            let AlignedBasesQualified = true
            let ReadAlignmentRatioQualified = true
            let BaseAlignmentRatioQualified = true
            let TargetedReadsQualified = true
            let TargetedBasesQualified = true
            let TargetedReadsRatioQualified = true
            let TargetedBasesRatioQualified = true

            data.forEach((item: any) => {
              const rule = qcRules?.find((items: any) => items.key === item)
              if (rule && rule.value) {
                const op = rule.op

                if (op === 'gte') {
                  if (item === 'Total number of bases') {
                    TotalBasesQualified = items?.total_bases >= rule.value
                  } else if (item === 'Raw reads') {
                    RawReadsQualified = items?.total_reads >= rule.value
                  } else if (item === 'filtered read count') {
                    FilteredReadsQualified =
                      items?.total_reads_q20 >= rule.value
                  } else if (item === 'filtered base count') {
                    FilteredBasesQualified =
                      items?.total_bases_q20 >= rule.value
                  } else if (item === 'read filtering retention ratio') {
                    ReadFilteringRetentionRatioQualified =
                      items?.total_reads_q20 / items?.total_reads >= rule.value
                  } else if (item === 'base filtering retention ratio') {
                    BaseFilteringRetentionRatioQualified =
                      items?.total_bases_q20 / items?.total_bases >= rule.value
                  } else if (item === 'aligned read count') {
                    AlignedReadsQualified = items?.reads_mapped >= rule.value
                  } else if (item === 'aligned base count') {
                    AlignedBasesQualified = items?.bases_mapped >= rule.value
                  } else if (item === 'read alignment ratio') {
                    ReadAlignmentRatioQualified =
                      items?.reads_mapped / items?.total_reads >= rule.value
                  } else if (item === 'base alignment ratio') {
                    BaseAlignmentRatioQualified =
                      items?.bases_mapped / items?.total_bases >= rule.value
                  } else if (item === 'targeted read count') {
                    TargetedReadsQualified =
                      items?.reads_on_target >= rule.value
                  } else if (item === 'targeted base count') {
                    TargetedBasesQualified =
                      items?.bases_on_target >= rule.value
                  } else if (item === 'read targeting ratio') {
                    TargetedReadsRatioQualified =
                      items?.reads_on_target / items?.total_reads >= rule.value
                  } else if (item === 'base targeting ratio') {
                    TargetedBasesRatioQualified =
                      items?.bases_on_target / items?.total_bases >= rule.value
                  }
                } else if (op === 'lte') {
                  if (item === 'Total number of bases') {
                    TotalBasesQualified = items?.total_bases <= rule.value
                  } else if (item === 'Raw reads') {
                    RawReadsQualified = items?.total_reads <= rule.value
                  } else if (item === 'filtered read count') {
                    FilteredReadsQualified =
                      items?.total_reads_q20 <= rule.value
                  } else if (item === 'filtered base count') {
                    FilteredBasesQualified =
                      items?.total_bases_q20 <= rule.value
                  } else if (item === 'read filtering retention ratio') {
                    ReadFilteringRetentionRatioQualified =
                      items?.total_reads_q20 / items?.total_reads <= rule.value
                  } else if (item === 'base filtering retention ratio') {
                    BaseFilteringRetentionRatioQualified =
                      items?.total_bases_q20 / items?.total_bases <= rule.value
                  } else if (item === 'aligned read count') {
                    AlignedReadsQualified = items?.reads_mapped <= rule.value
                  } else if (item === 'aligned base count') {
                    AlignedBasesQualified = items?.bases_mapped <= rule.value
                  } else if (item === 'read alignment ratio') {
                    ReadAlignmentRatioQualified =
                      items?.reads_mapped / items?.total_reads <= rule.value
                  } else if (item === 'base alignment ratio') {
                    BaseAlignmentRatioQualified =
                      items?.bases_mapped / items?.total_bases <= rule.value
                  } else if (item === 'targeted read count') {
                    TargetedReadsQualified =
                      items?.reads_on_target <= rule.value
                  } else if (item === 'targeted base count') {
                    TargetedBasesQualified =
                      items?.bases_on_target <= rule.value
                  } else if (item === 'read targeting ratio') {
                    TargetedReadsRatioQualified =
                      items?.reads_on_target / items?.total_reads <= rule.value
                  } else if (item === 'base targeting ratio') {
                    TargetedBasesRatioQualified =
                      items?.bases_on_target / items?.total_bases <= rule.value
                  }
                }
              }
            })
            const TotalBasesRule = qcRules?.find(
              (item: any) => item.key === 'Total number of bases'
            )
            const RawReadsRule = qcRules?.find(
              (item: any) => item.key === 'Raw reads'
            )
            const FilteredReadsRule = qcRules?.find(
              (item: any) => item.key === 'filtered read count'
            )
            const FilteredBasesRule = qcRules?.find(
              (item: any) => item.key === 'filtered base count'
            )
            const ReadFilteringRetentionRatioRule = qcRules?.find(
              (item: any) => item.key === 'read filtering retention ratio'
            )
            const BaseFilteringRetentionRatioRule = qcRules?.find(
              (item: any) => item.key === 'base filtering retention ratio'
            )
            const AlignedReadsRule = qcRules?.find(
              (item: any) => item.key === 'aligned read count'
            )
            const AlignedBasesRule = qcRules?.find(
              (item: any) => item.key === 'aligned base count'
            )
            const ReadAlignmentRatioRule = qcRules?.find(
              (item: any) => item.key === 'read alignment ratio'
            )
            const BaseAlignmentRatioRule = qcRules?.find(
              (item: any) => item.key === 'base alignment ratio'
            )
            const TargetedReadsRule = qcRules?.find(
              (item: any) => item.key === 'targeted read count'
            )
            const TargetedBasesRule = qcRules?.find(
              (item: any) => item.key === 'targeted base count'
            )
            const TargetedReadsRatioRule = qcRules?.find(
              (item: any) => item.key === 'read targeting ratio'
            )
            const TargetedBasesRatioRule = qcRules?.find(
              (item: any) => item.key === 'base targeting ratio'
            )

            return [
              {
                key: 'Sample Id',
                label: t('Sample Id'),
                children: items.id
              },
              {
                key: 'Total valid data volume',
                label: t('Total valid data volume'),
                children: items.total_bases
                  ? `${
                      (items.total_bases / 1024 / 1024 / 1024).toFixed(2) + 'Gb'
                    }`
                  : '-'
              },
              {
                key: 'Target region length',
                label: t('Target region length'),
                children: items?.target_territory
                  ? formatNumberWithCommas(items?.target_territory)
                  : '-'
              },
              {
                key: 'Targeted effective data volume',
                label: t('Targeted effective data volume'),
                children: items?.bases_on_target
                  ? formatNumberWithCommas(items?.bases_on_target)
                  : '-'
              },
              {
                key:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? '1X/10X/20X/30X/40X/50X'
                    : '1X/2X/10X/20X/30X',
                label:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? `${t('1X/10X/20X/30X/40X/50X')} ${t('Coverage')}`
                    : `${t('1X/2X/10X/20X/30X')} ${t('Coverage')}`,
                children:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? `${(items.pct_wgs_1x * 100).toFixed(2)}%/${(
                        items.pct_wgs_10x * 100
                      ).toFixed(2)}%/${(items.pct_wgs_20x * 100).toFixed(
                        2
                      )}%/${(items.pct_wgs_30x * 100).toFixed(2)}%/${(
                        items.pct_wgs_40x * 100
                      ).toFixed(2)}%/${(items.pct_wgs_50x * 100).toFixed(2)}%`
                    : `${(items.pct_target_1x * 100).toFixed(2)}%/${(
                        items.pct_target_2x * 100
                      ).toFixed(2)}%/${(items.pct_target_10x * 100).toFixed(
                        2
                      )}%/${(items.pct_target_20x * 100).toFixed(2)}%/${(
                        items.pct_target_30x * 100
                      ).toFixed(2)}%`,
                value:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? `${(items.pct_wgs_1x * 100).toFixed(2)}%/${(
                        items.pct_wgs_10x * 100
                      ).toFixed(2)}%/${(items.pct_wgs_20x * 100).toFixed(
                        2
                      )}%/${(items.pct_wgs_30x * 100).toFixed(2)}%/${(
                        items.pct_wgs_40x * 100
                      ).toFixed(2)}%/${(items.pct_wgs_50x * 100).toFixed(2)}%`
                    : `${(items.pct_target_1x * 100).toFixed(2)}%/${(
                        items.pct_target_2x * 100
                      ).toFixed(2)}%/${(items.pct_target_10x * 100).toFixed(
                        2
                      )}%/${(items.pct_target_20x * 100).toFixed(2)}%/${(
                        items.pct_target_30x * 100
                      ).toFixed(2)}%`
              },
              {
                key: 'Intron 20X coverage',
                label: t('Intron 20X coverage'),
                children: `${
                  items?.intron_pct_20x
                    ? `${items?.intron_pct_20x.toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'Mitochondrial 20x coverage',
                label: t('Mitochondrial 20x coverage'),
                children: `${
                  items?.mt_pct_20x || items?.mt_pct_20x === 0
                    ? `${(items?.mt_pct_20x * 100).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key:
                  items.vcfType === 'wgs'
                    ? 'Average sequencing depth (X)'
                    : 'Targeted average sequencing depth',
                label:
                  items.vcfType === 'wgs'
                    ? t('Average sequencing depth (X)')
                    : t('Targeted average sequencing depth'),
                children: `${
                  items?.mean_target_coverage
                    ? `${Number(items?.mean_target_coverage).toFixed(2)}X`
                    : ''
                }`
              },
              {
                key: 'Average Variant Depth',
                label: t('Average Variant Depth'),

                children: `${
                  items?.avgReadDepth
                    ? `${items?.avgReadDepth.toFixed(2)}X`
                    : ''
                }`
              },
              {
                key: 'Intron mean depth',
                label: t('Intron mean depth'),
                children: `${
                  items?.intron_mean_depth ? `${items?.intron_mean_depth}X` : ''
                }`
              },
              {
                key: 'Mean mitochondrial depth',
                label: t('Mean mitochondrial depth'),
                children: `${
                  items?.mt_mean_depth || items?.mt_mean_depth === 0
                    ? `${items?.mt_mean_depth}X`
                    : ''
                }`
              },
              {
                key: 'Total number of bases',
                label: !TotalBasesQualified ? (
                  <Tooltip
                    title={`rule: ${
                      TotalBasesRule.op === 'gte' ? '>=' : '<='
                    } ${TotalBasesRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: TotalBasesQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('Total number of bases')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('Total number of bases')
                ),
                children: items?.total_bases
                  ? formatNumberWithCommas(items?.total_bases)
                  : ''
              },
              {
                key: 'Raw reads',
                label: !RawReadsQualified ? (
                  <Tooltip
                    title={`rule: ${RawReadsRule.op === 'gte' ? '>=' : '<='} ${
                      RawReadsRule.value
                    }`}
                  >
                    <Typography
                      sx={{
                        color: RawReadsQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('Raw reads')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('Raw reads')
                ),
                children: `${
                  items?.total_reads
                    ? formatNumberWithCommas(items?.total_reads)
                    : ''
                }`
              },
              {
                key: 'aligned read count',
                label: !AlignedReadsQualified ? (
                  <Tooltip
                    title={`rule: ${
                      AlignedReadsRule.op === 'gte' ? '>=' : '<='
                    } ${AlignedReadsRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: AlignedReadsQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('aligned read count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('aligned read count')
                ),
                children: `${
                  items?.reads_mapped
                    ? formatNumberWithCommas(items?.reads_mapped)
                    : ''
                }`
              },
              {
                key: 'aligned base count',
                label: !AlignedBasesQualified ? (
                  <Tooltip
                    title={`rule: ${
                      AlignedBasesRule.op === 'gte' ? '>=' : '<='
                    } ${AlignedBasesRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: AlignedBasesQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('aligned bases count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('aligned bases count')
                ),
                children: `${
                  items?.bases_mapped
                    ? formatNumberWithCommas(items?.bases_mapped)
                    : ''
                }`
              },
              {
                key: 'read alignment ratio',
                label: !ReadAlignmentRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      ReadAlignmentRatioRule.op === 'gte' ? '>=' : '<='
                    } ${ReadAlignmentRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: ReadAlignmentRatioQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('read alignment ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('read alignment ratio')
                ),
                children: `${
                  items?.reads_mapped
                    ? `${(
                        (items?.reads_mapped / items?.total_reads) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'base alignment ratio',
                label: !BaseAlignmentRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      BaseAlignmentRatioRule.op === 'gte' ? '>=' : '<='
                    } ${BaseAlignmentRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: BaseAlignmentRatioQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('bases alignment ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('bases alignment ratio')
                ),
                children: `${
                  items?.bases_mapped
                    ? `${(
                        (items?.bases_mapped / items?.total_bases) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'filtered read count',
                label: !FilteredReadsQualified ? (
                  <Tooltip
                    title={`rule: ${
                      FilteredReadsRule.op === 'gte' ? '>=' : '<='
                    } ${FilteredReadsRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: FilteredReadsQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('filtered read count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('filtered read count')
                ),
                children: `${
                  items?.total_reads_q20
                    ? formatNumberWithCommas(items?.total_reads_q20)
                    : ''
                }`
              },
              {
                key: 'filtered base count',
                label: !FilteredBasesQualified ? (
                  <Tooltip
                    title={`rule: ${
                      FilteredBasesRule.op === 'gte' ? '>=' : '<='
                    } ${FilteredBasesRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: FilteredBasesQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('filtered bases count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('filtered bases count')
                ),
                children: `${
                  items?.total_bases_q20
                    ? formatNumberWithCommas(items?.total_bases_q20)
                    : ''
                }`
              },
              {
                key: 'read filtering retention ratio',
                label: !ReadFilteringRetentionRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      ReadFilteringRetentionRatioRule.op === 'gte' ? '>=' : '<='
                    } ${ReadFilteringRetentionRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: ReadFilteringRetentionRatioQualified
                          ? 'inherit'
                          : 'red'
                      }}
                    >
                      {t('read filtering retention ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('read filtering retention ratio')
                ),
                children: `${
                  items?.total_reads_q20
                    ? `${(
                        (items?.total_reads_q20 / items?.total_reads) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'base filtering retention ratio',
                label: !BaseFilteringRetentionRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      BaseFilteringRetentionRatioRule.op === 'gte' ? '>=' : '<='
                    } ${BaseFilteringRetentionRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: BaseFilteringRetentionRatioQualified
                          ? 'inherit'
                          : 'red'
                      }}
                    >
                      {t('bases filtering retention ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('bases filtering retention ratio')
                ),
                children: `${
                  items?.total_bases_q20
                    ? `${(
                        (items?.total_bases_q20 / items?.total_bases) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'targeted read count',
                label: !TargetedReadsQualified ? (
                  <Tooltip
                    title={`rule: ${
                      TargetedReadsRule.op === 'gte' ? '>=' : '<='
                    } ${TargetedReadsRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: TargetedReadsQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('targeted read count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('targeted read count')
                ),
                children: `${
                  items?.reads_on_target
                    ? formatNumberWithCommas(items?.reads_on_target)
                    : ''
                }`
              },
              {
                key: 'targeted base count',
                label: !TargetedBasesQualified ? (
                  <Tooltip
                    title={`rule: ${
                      TargetedBasesRule.op === 'gte' ? '>=' : '<='
                    } ${TargetedBasesRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: TargetedBasesQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('targeted bases count')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('targeted bases count')
                ),
                children: `${
                  items?.bases_on_target
                    ? formatNumberWithCommas(items?.bases_on_target)
                    : ''
                }`
              },
              {
                key: 'read targeting ratio',
                label: !TargetedReadsRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      TargetedReadsRatioRule.op === 'gte' ? '>=' : '<='
                    } ${TargetedReadsRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: TargetedReadsRatioQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('read targeting ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('read targeting ratio')
                ),
                children: `${
                  items?.reads_on_target
                    ? `${(
                        (items?.reads_on_target / items?.total_reads) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'base targeting ratio',
                label: !TargetedBasesRatioQualified ? (
                  <Tooltip
                    title={`rule: ${
                      TargetedBasesRatioRule.op === 'gte' ? '>=' : '<='
                    } ${TargetedBasesRatioRule.value}`}
                  >
                    <Typography
                      sx={{
                        color: TargetedBasesRatioQualified ? 'inherit' : 'red'
                      }}
                    >
                      {t('bases targeting ratio')}
                    </Typography>
                  </Tooltip>
                ) : (
                  t('bases targeting ratio')
                ),
                children: `${
                  items?.bases_on_target
                    ? `${(
                        (items?.bases_on_target / items?.total_bases) *
                        100
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'PCR duplicate',
                label: t('PCR duplicate'),
                children: `${(items?.pcr_duplicate_rate * 100).toFixed(2)}%`
              },

              // {
              //   key: 'Mean depth of target region',
              //   label: t('Mean depth of target region'),
              //   children: items?.fastqc?.metricsData?.targetMeanDepth
              // },
              {
                key: 'Rate of nucleotide mismatch',
                label: t('Rate of nucleotide mismatch'),
                children: `${
                  items?.mismatch_rate
                    ? `${Number(items?.mismatch_rate * 100).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'TS/TV ratio',
                label: t('TS/TV ratio'),
                children: `${
                  items?.ts_tv_ratio
                    ? `${Number(items?.ts_tv_ratio).toFixed(2)}`
                    : ''
                }`
              },
              {
                key: 'Efficiency of targeted capture',
                label: t('Efficiency of targeted capture'),
                children: items?.capture_efficiency
                  ? `${Number(items?.capture_efficiency * 100).toFixed(2)}%`
                  : ''
              },
              {
                key: 'Duplication rate',
                label: t('Duplication rate'),
                children: items?.pcr_duplicate_rate
                  ? `${Number(items?.pcr_duplicate_rate * 100).toFixed(2)}%`
                  : ''
              },
              {
                key: 'Sample contamination',
                label: (
                  <Stack
                    spacing={4}
                    direction={'column'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 250 }}>
                          <Typography color={'white'} fontSize={14}>
                            {`< 0.01: ${t('No problem')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`0.01-0.03: ${t('Mild contamination')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`0.03–0.05: ${t('Moderate contamination')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`> 0.05: ${t('High contamination')}`}
                          </Typography>
                        </Box>
                      }
                      placement={'top-start'}
                    >
                      <Stack
                        spacing={4}
                        direction={'row'}
                        height={'100%'}
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            color: 'rgba(0, 0, 0, 0.65)',
                            fontSize: '14px',
                            fontWeight: 600
                          }}
                        >
                          {t('Sample contamination')}
                        </Typography>

                        <IconButton>
                          <HelpCircleOutline />
                        </IconButton>
                      </Stack>
                    </Tooltip>

                    <Stack direction={'row'} alignItems="center">
                      <TextField
                        size="small"
                        placeholder={t(
                          'Customize the pollution ratio threshold'
                        )}
                        label={t('Customize the pollution ratio threshold')}
                        sx={{
                          maxWidth: 160
                        }}
                        value={ratio}
                        onChange={(e) => {
                          // if (Number(e.target.value) > 5) {
                          //   setRatio(5)
                          // } else if (Number(e.target.value) < 1) {
                          //   setRatio(1)
                          // } else {
                          //   setRatio(Number(e.target.value))
                          // }
                          setRatio(Number(e.target.value))
                        }}
                      />
                      %
                    </Stack>
                  </Stack>
                ),
                children:
                  items?.freemix && items?.freemix < 0.01 ? (
                    `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                      'No DNA contamination'
                    )}`
                  ) : items?.freemix > ratio / 100 ? (
                    <span
                      style={{
                        color: 'red'
                      }}
                    >{`(${items?.freemix})${t('DNA contamination')}`}</span>
                  ) : items?.freemix > 0.01 && items?.freemix < ratio / 100 ? (
                    <span
                      style={{
                        color: 'green'
                      }}
                    >{`(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                      'Possible DNA contamination'
                    )}`}</span>
                  ) : (
                    ''
                  ),
                value:
                  items?.freemix && items?.freemix < 0.01
                    ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                        'No DNA contamination'
                      )}`
                    : items?.freemix > ratio / 100
                    ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                        'DNA contamination'
                      )}`
                    : items?.freemix > 0.01 && items?.freemix < ratio / 100
                    ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                        'Possible DNA contamination'
                      )}`
                    : ''
              },
              {
                key: 'fold-80',
                label: (
                  <Stack
                    spacing={4}
                    direction={'row'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {t('fold-80')}
                    </Typography>

                    <IconButton onClick={handlePopoverOpen}>
                      <HelpCircleOutline />
                    </IconButton>
                  </Stack>
                ),
                children: `${
                  items?.fold_80_base_penalty
                    ? Number(items?.fold_80_base_penalty).toFixed(2)
                    : ''
                }`
              },
              {
                key: 'q30',
                label: (
                  <Stack
                    spacing={4}
                    direction={'row'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {t('Q30')}
                    </Typography>
                  </Stack>
                ),
                children: (
                  <Box>
                    <DataGrid
                      rows={(items?.fastq_qc && Array.isArray(items?.fastq_qc)
                        ? // eslint-disable-next-line no-unsafe-optional-chaining
                          items?.fastq_qc
                        : []
                      ).map((item: any, index: number) => ({
                        ...item,
                        id: index
                      }))}
                      columns={q30Column}
                      disableColumnMenu
                      hideFooterPagination
                      sx={{
                        '& .MuiDataGrid-footerContainer': {
                          justifyContent: 'center',
                          display: 'none'
                        }
                      }}
                    />
                  </Box>
                )
              }
            ]
          } else {
            return [
              {
                key: 'Sample Id',
                label: t('Sample Id'),
                children: items.id
              },
              {
                key: 'Total valid data volume',
                label: t('Total valid data volume'),
                children:
                  items.fileSize > 1000
                    ? `${(items.fileSize / 1024).toFixed(2)}G`
                    : `${items.fileSize}MB`
              },
              {
                key: 'Target region length',
                label: t('Target region length'),
                children: items?.fastqc?.metricsData?.targetRegionLength
              },
              {
                key:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? '1X/10X/20X/30X/40X/50X'
                    : '1x/4x/10x/20x/30x',
                label:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? `${t('1X/10X/20X/30X/40X/50X')} ${t('Coverage')}`
                    : `${t('1x/4x/10x/20x/30x')} ${t('Coverage')}`,
                children:
                  items.sampleType === 'fastq' &&
                  items.vcfType === 'wgs' &&
                  Array.isArray(items?.fastqc?.barChartData) ? (
                    `${items?.fastqc?.barChartData
                      ?.map((item: any) => `${item.value}%`)
                      .join('/')}`
                  ) : Array.isArray(
                      items?.fastqc?.metricsData?.targetRegionAverageDepths
                    ) ? (
                    <>
                      {`${
                        items?.fastqc?.metricsData?.targetRegionCoverage
                          ? `${items?.fastqc?.metricsData?.targetRegionCoverage}%`
                          : ''
                      }/${items?.fastqc?.metricsData?.targetRegionAverageDepths
                        .map((item: any) => `${item.value}%`)
                        .join('/')}`}
                    </>
                  ) : (
                    items?.fastqc?.metricsData?.targetRegionAverageDepths
                  ),
                value:
                  items.sampleType === 'fastq' &&
                  items.vcfType === 'wgs' &&
                  Array.isArray(items?.fastqc?.barChartData)
                    ? `${items?.fastqc?.barChartData
                        ?.map((item: any) => `${item.value}%`)
                        .join('/')}`
                    : Array.isArray(
                        items?.fastqc?.metricsData?.targetRegionAverageDepths
                      )
                    ? `${
                        items?.fastqc?.metricsData?.targetRegionCoverage
                      }/${items?.fastqc?.metricsData?.targetRegionAverageDepths
                        .map((item: any) => `${item.value}%`)
                        .join('/')}`
                    : items?.fastqc?.metricsData?.targetRegionAverageDepths
              },
              {
                key:
                  items.vcfType === 'wgs'
                    ? 'Average sequencing depth (X)'
                    : 'Targeted average sequencing depth',
                label:
                  items.vcfType === 'wgs'
                    ? t('Average sequencing depth (X)')
                    : t('Targeted average sequencing depth'),
                children:
                  items.vcfType === 'wgs'
                    ? `${
                        items?.fastqc?.metricsData?.meanCoverage
                          ? `${items?.fastqc?.metricsData?.meanCoverage}X`
                          : ''
                      }`
                    : `${
                        items?.fastqc?.metricsData?.targetMeanDepth
                          ? `${items?.fastqc?.metricsData?.targetMeanDepth}X`
                          : ''
                      }`
              },
              {
                key: 'Average Variant Depth',
                label: t('Average Variant Depth'),
                children: `${
                  items?.avgReadDepth
                    ? `${items?.avgReadDepth.toFixed(2)}X`
                    : ''
                }`
              },
              {
                key: 'Intron mean depth',
                label: t('Intron mean depth'),
                children: `${
                  items?.fastqc?.metricsData?.intronMeanDepth
                    ? `${items?.fastqc?.metricsData?.intronMeanDepth}X`
                    : ''
                }`
              },
              {
                key: 'Intron 20X coverage',
                label: t('Intron 20X coverage'),
                children: `${
                  items?.fastqc?.metricsData?.intron20X
                    ? `${items?.fastqc?.metricsData?.intron20X}%`
                    : ''
                }`
              },
              {
                key: 'Mean mitochondrial depth',
                label: t('Mean mitochondrial depth'),
                children: `${
                  items?.fastqc?.metricsData?.mtMeanDepth
                    ? `${items?.fastqc?.metricsData?.mtMeanDepth}X`
                    : ''
                }`
              },
              {
                key: 'Mitochondrial 20x coverage',
                label: t('Mitochondrial 20x coverage'),
                children: `${
                  items?.fastqc?.metricsData?.mt20X
                    ? `${items?.fastqc?.metricsData?.mt20X}%`
                    : ''
                }`
              },
              {
                key: 'Total number of bases',
                label: t('Total number of bases'),
                children: items?.fastqc?.metricsData?.rawReads
                  ? `${
                      Math.round(
                        (Number(
                          `${calculateAndFormat(
                            `${items?.fastqc?.metricsData?.rawReads}`,
                            150
                          )}`.replace(/,/g, '')
                        ) /
                          1e9) *
                          100
                      ) / 100
                    }G`
                  : ''
              },
              {
                key: 'Raw reads',
                label: t('Raw reads'),
                children: `${
                  items?.fastqc?.metricsData?.rawReads
                    ? items?.fastqc?.metricsData?.rawReads
                    : ''
                }`
              },
              {
                key: 'Reads mapped to genome',
                label: t('Reads mapped to genome'),
                children: `${
                  items?.fastqc?.metricsData?.readsMapped
                    ? items?.fastqc?.metricsData?.readsMapped
                    : ''
                }`
              },
              {
                key: 'mappingrate',
                label: t('mappingrate'),
                children:
                  items?.fastqc?.metricsData?.rawReads &&
                  items?.fastqc?.metricsData?.readsMapped
                    ? `${
                        Number(
                          (
                            items?.fastqc?.metricsData?.readsMapped
                              .split(',')
                              .join('') /
                            items?.fastqc?.metricsData?.rawReads
                              .split(',')
                              .join('')
                          ).toFixed(4)
                        ) * 100
                      }%`
                    : ''
              },
              {
                key: 'Reads mapped target region',
                label: t('Reads mapped target region'),
                children:
                  items.sampleType === 'fastq' && items.vcfType === 'wgs'
                    ? items?.fastqc?.metricsData?.readsMapped
                    : items?.fastqc?.metricsData?.readsMappedOnTargetRegion
              },
              {
                key: 'PCR duplicate',
                label: t('PCR duplicate'),
                children: items?.fastqc?.metricsData?.pcrDuplicateRate
              },

              // {
              //   key: 'Mean depth of target region',
              //   label: t('Mean depth of target region'),
              //   children: items?.fastqc?.metricsData?.targetMeanDepth
              // },
              {
                key: 'Rate of nucleotide mismatch',
                label: t('Rate of nucleotide mismatch'),
                children: `${
                  items?.fastqc?.metricsData?.mismatchRate
                    ? `${Number(
                        items?.fastqc?.metricsData?.mismatchRate
                      ).toFixed(2)}%`
                    : ''
                }`
              },
              {
                key: 'TS/TV ratio',
                label: t('TS/TV ratio'),
                children: `${
                  items?.tstv?.ratio
                    ? `${Number(items?.tstv?.ratio).toFixed(2)}`
                    : ''
                }`
              },
              {
                key: 'Efficiency of targeted capture',
                label: t('Efficiency of targeted capture'),
                children:
                  items?.fastqc?.metricsData?.readsOnTargetRegionsPercent
              },
              {
                key: 'Duplication rate',
                label: t('Duplication rate'),
                children: items?.fastqc?.metricsData?.readsAfterDedupedPercent
              },
              {
                key: 'Sample contamination',
                label: (
                  <Stack
                    spacing={4}
                    direction={'column'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 250 }}>
                          <Typography color={'white'} fontSize={14}>
                            {`< 0.01: ${t('No problem')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`0.01-0.03: ${t('Mild contamination')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`0.03–0.05: ${t('Moderate contamination')}`}
                          </Typography>

                          <Typography color={'white'} fontSize={14}>
                            {`> 0.05: ${t('High contamination')}`}
                          </Typography>
                        </Box>
                      }
                      placement={'top-start'}
                    >
                      <Stack
                        spacing={4}
                        direction={'row'}
                        height={'100%'}
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            color: 'rgba(0, 0, 0, 0.65)',
                            fontSize: '14px',
                            fontWeight: 600
                          }}
                        >
                          {t('Sample contamination')}
                        </Typography>

                        <IconButton>
                          <HelpCircleOutline />
                        </IconButton>
                      </Stack>
                    </Tooltip>

                    <Stack direction={'row'} alignItems="center">
                      <TextField
                        size="small"
                        placeholder={t(
                          'Customize the pollution ratio threshold'
                        )}
                        label={t('Customize the pollution ratio threshold')}
                        sx={{
                          maxWidth: 160
                        }}
                        value={ratio}
                        onChange={(e) => {
                          // if (Number(e.target.value) > 5) {
                          //   setRatio(5)
                          // } else if (Number(e.target.value) < 1) {
                          //   setRatio(1)
                          // } else {
                          //   setRatio(Number(e.target.value))
                          // }
                          setRatio(Number(e.target.value))
                        }}
                      />
                      %
                    </Stack>
                  </Stack>
                ),
                children:
                  items?.freemixScore && items?.freemixScore < 0.01 ? (
                    `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                      'No DNA contamination'
                    )}`
                  ) : items?.freemixScore > ratio / 100 ? (
                    <span
                      style={{
                        color: 'red'
                      }}
                    >{`(${items?.freemixScore})${t(
                      'DNA contamination'
                    )}`}</span>
                  ) : items?.freemixScore > 0.01 &&
                    items?.freemixScore < ratio / 100 ? (
                    <span
                      style={{
                        color: 'green'
                      }}
                    >{`(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                      'Possible DNA contamination'
                    )}`}</span>
                  ) : (
                    ''
                  ),
                value:
                  items?.freemixScore && items?.freemixScore < 0.01
                    ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                        'No DNA contamination'
                      )}`
                    : items?.freemixScore > ratio / 100
                    ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                        'DNA contamination'
                      )}`
                    : items?.freemixScore > 0.01 &&
                      items?.freemixScore < ratio / 100
                    ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                        'Possible DNA contamination'
                      )}`
                    : ''
              },
              {
                key: 'fold-80',
                label: (
                  <Stack
                    spacing={4}
                    direction={'row'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {t('fold-80')}
                    </Typography>

                    <IconButton onClick={handlePopoverOpen}>
                      <HelpCircleOutline />
                    </IconButton>
                  </Stack>
                ),
                children: `${items?.fold80 ? items?.fold80 : ''}`
              },
              {
                key: 'q30',
                label: (
                  <Stack
                    spacing={4}
                    direction={'row'}
                    height={'100%'}
                    alignItems="center"
                    justifyContent={'center'}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {t('Q30')}
                    </Typography>
                  </Stack>
                ),
                children: (
                  <Box>
                    <DataGrid
                      rows={(items?.fastqc?.metricsData?.seqtk_q30 &&
                      Array.isArray(items?.fastqc?.metricsData?.seqtk_q30)
                        ? // eslint-disable-next-line no-unsafe-optional-chaining
                          items?.fastqc?.metricsData?.seqtk_q30
                        : []
                      ).map((item: any, index: number) => ({
                        ...item,
                        id: index
                      }))}
                      columns={q30Column}
                      disableColumnMenu
                      hideFooterPagination
                      sx={{
                        '& .MuiDataGrid-footerContainer': {
                          justifyContent: 'center',
                          display: 'none'
                        }
                      }}
                    />
                  </Box>
                )
              }
            ]
          }
        })
      } else
        return samples.map((items: any) => {
          if (items.pipelineVersion) {
            const qcRules = projects?.find(
              (item: any) => item.id === items.projectId
            )?.qcRules
            const data = [
              'Total number of bases',
              'Raw reads',
              'filtered read count',
              'filtered base count',
              'read filtering retention ratio',
              'base filtering retention ratio',
              'aligned read count',
              'aligned base count',
              'read alignment ratio',
              'base alignment ratio',
              'targeted read count',
              'targeted base count',
              'read targeting ratio',
              'base targeting ratio'
            ]
            let TotalBasesQualified = true
            let RawReadsQualified = true
            let FilteredReadsQualified = true
            let FilteredBasesQualified = true
            let ReadFilteringRetentionRatioQualified = true
            let BaseFilteringRetentionRatioQualified = true
            let AlignedReadsQualified = true
            let AlignedBasesQualified = true
            let ReadAlignmentRatioQualified = true
            let BaseAlignmentRatioQualified = true
            let TargetedReadsQualified = true
            let TargetedBasesQualified = true
            let TargetedReadsRatioQualified = true
            let TargetedBasesRatioQualified = true

            data.forEach((item: any) => {
              const rule = qcRules?.find((items: any) => items.key === item)
              if (rule && rule.value) {
                const op = rule.op

                if (op === 'gte') {
                  if (item === 'Total number of bases') {
                    TotalBasesQualified = items?.total_bases >= rule.value
                  } else if (item === 'Raw reads') {
                    RawReadsQualified = items?.total_reads >= rule.value
                  } else if (item === 'filtered read count') {
                    FilteredReadsQualified =
                      items?.total_reads_q20 >= rule.value
                  } else if (item === 'filtered base count') {
                    FilteredBasesQualified =
                      items?.total_bases_q20 >= rule.value
                  } else if (item === 'read filtering retention ratio') {
                    ReadFilteringRetentionRatioQualified =
                      items?.total_reads_q20 / items?.total_reads >= rule.value
                  } else if (item === 'base filtering retention ratio') {
                    BaseFilteringRetentionRatioQualified =
                      items?.total_bases_q20 / items?.total_bases >= rule.value
                  } else if (item === 'aligned read count') {
                    AlignedReadsQualified = items?.reads_mapped >= rule.value
                  } else if (item === 'aligned base count') {
                    AlignedBasesQualified = items?.bases_mapped >= rule.value
                  } else if (item === 'read alignment ratio') {
                    ReadAlignmentRatioQualified =
                      items?.reads_mapped / items?.total_reads >= rule.value
                  } else if (item === 'base alignment ratio') {
                    BaseAlignmentRatioQualified =
                      items?.bases_mapped / items?.total_bases >= rule.value
                  } else if (item === 'targeted read count') {
                    TargetedReadsQualified =
                      items?.reads_on_target >= rule.value
                  } else if (item === 'targeted base count') {
                    TargetedBasesQualified =
                      items?.bases_on_target >= rule.value
                  } else if (item === 'read targeting ratio') {
                    TargetedReadsRatioQualified =
                      items?.reads_on_target / items?.total_reads >= rule.value
                  } else if (item === 'base targeting ratio') {
                    TargetedBasesRatioQualified =
                      items?.bases_on_target / items?.total_bases >= rule.value
                  }
                } else if (op === 'lte') {
                  if (item === 'Total number of bases') {
                    TotalBasesQualified = items?.total_bases <= rule.value
                  } else if (item === 'Raw reads') {
                    RawReadsQualified = items?.total_reads <= rule.value
                  } else if (item === 'filtered read count') {
                    FilteredReadsQualified =
                      items?.total_reads_q20 <= rule.value
                  } else if (item === 'filtered base count') {
                    FilteredBasesQualified =
                      items?.total_bases_q20 <= rule.value
                  } else if (item === 'read filtering retention ratio') {
                    ReadFilteringRetentionRatioQualified =
                      items?.total_reads_q20 / items?.total_reads <= rule.value
                  } else if (item === 'base filtering retention ratio') {
                    BaseFilteringRetentionRatioQualified =
                      items?.total_bases_q20 / items?.total_bases <= rule.value
                  } else if (item === 'aligned read count') {
                    AlignedReadsQualified = items?.reads_mapped <= rule.value
                  } else if (item === 'aligned base count') {
                    AlignedBasesQualified = items?.bases_mapped <= rule.value
                  } else if (item === 'read alignment ratio') {
                    ReadAlignmentRatioQualified =
                      items?.reads_mapped / items?.total_reads <= rule.value
                  } else if (item === 'base alignment ratio') {
                    BaseAlignmentRatioQualified =
                      items?.bases_mapped / items?.total_bases <= rule.value
                  } else if (item === 'targeted read count') {
                    TargetedReadsQualified =
                      items?.reads_on_target <= rule.value
                  } else if (item === 'targeted base count') {
                    TargetedBasesQualified =
                      items?.bases_on_target <= rule.value
                  } else if (item === 'read targeting ratio') {
                    TargetedReadsRatioQualified =
                      items?.reads_on_target / items?.total_reads <= rule.value
                  } else if (item === 'base targeting ratio') {
                    TargetedBasesRatioQualified =
                      items?.bases_on_target / items?.total_bases <= rule.value
                  }
                }
              }
            })
            const TotalBasesRule = qcRules?.find(
              (item: any) => item.key === 'Total number of bases'
            )
            const RawReadsRule = qcRules?.find(
              (item: any) => item.key === 'Raw reads'
            )
            const FilteredReadsRule = qcRules?.find(
              (item: any) => item.key === 'filtered read count'
            )
            const FilteredBasesRule = qcRules?.find(
              (item: any) => item.key === 'filtered base count'
            )
            const ReadFilteringRetentionRatioRule = qcRules?.find(
              (item: any) => item.key === 'read filtering retention ratio'
            )
            const BaseFilteringRetentionRatioRule = qcRules?.find(
              (item: any) => item.key === 'base filtering retention ratio'
            )
            const AlignedReadsRule = qcRules?.find(
              (item: any) => item.key === 'aligned read count'
            )
            const AlignedBasesRule = qcRules?.find(
              (item: any) => item.key === 'aligned base count'
            )
            const ReadAlignmentRatioRule = qcRules?.find(
              (item: any) => item.key === 'read alignment ratio'
            )
            const BaseAlignmentRatioRule = qcRules?.find(
              (item: any) => item.key === 'base alignment ratio'
            )
            const TargetedReadsRule = qcRules?.find(
              (item: any) => item.key === 'targeted read count'
            )
            const TargetedBasesRule = qcRules?.find(
              (item: any) => item.key === 'targeted base count'
            )
            const TargetedReadsRatioRule = qcRules?.find(
              (item: any) => item.key === 'read targeting ratio'
            )
            const TargetedBasesRatioRule = qcRules?.find(
              (item: any) => item.key === 'base targeting ratio'
            )

            return {
              name: items.type,
              value: [
                {
                  key: 'Sample Id',
                  label: t('Sample Id'),
                  children: items.id
                },
                {
                  key: 'Total valid data volume',
                  label: t('Total valid data volume'),
                  children: items.total_bases
                    ? `${
                        (items.total_bases / 1024 / 1024 / 1024).toFixed(2) +
                        'Gb'
                      }`
                    : '-'
                },
                {
                  key: 'Target region length',
                  label: t('Target region length'),
                  children: items?.target_territory
                    ? formatNumberWithCommas(items?.target_territory)
                    : '-'
                },
                {
                  key: 'Targeted effective data volume',
                  label: t('Targeted effective data volume'),
                  children: items?.bases_on_target
                    ? formatNumberWithCommas(items?.bases_on_target)
                    : '-'
                },
                {
                  key:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? '1X/10X/20X/30X/40X/50X'
                      : '1X/2X/10X/20X/30X',
                  label:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? `${t('1X/10X/20X/30X/40X/50X')} ${t('Coverage')}`
                      : `${t('1X/2X/10X/20X/30X')} ${t('Coverage')}`,
                  children:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? `${(items.pct_wgs_1x * 100).toFixed(2)}%/${(
                          items.pct_wgs_10x * 100
                        ).toFixed(2)}%/${(items.pct_wgs_20x * 100).toFixed(
                          2
                        )}%/${(items.pct_wgs_30x * 100).toFixed(2)}%/${(
                          items.pct_wgs_40x * 100
                        ).toFixed(2)}%/${(items.pct_wgs_50x * 100).toFixed(2)}%`
                      : `${(items.pct_target_1x * 100).toFixed(2)}%/${(
                          items.pct_target_2x * 100
                        ).toFixed(2)}%/${(items.pct_target_10x * 100).toFixed(
                          2
                        )}%/${(items.pct_target_20x * 100).toFixed(2)}%/${(
                          items.pct_target_30x * 100
                        ).toFixed(2)}%`,
                  value:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? `${(items.pct_wgs_1x * 100).toFixed(2)}%/${(
                          items.pct_wgs_10x * 100
                        ).toFixed(2)}%/${(items.pct_wgs_20x * 100).toFixed(
                          2
                        )}%/${(items.pct_wgs_30x * 100).toFixed(2)}%/${(
                          items.pct_wgs_40x * 100
                        ).toFixed(2)}%/${(items.pct_wgs_50x * 100).toFixed(2)}%`
                      : `${(items.pct_target_1x * 100).toFixed(2)}%/${(
                          items.pct_target_2x * 100
                        ).toFixed(2)}%/${(items.pct_target_10x * 100).toFixed(
                          2
                        )}%/${(items.pct_target_20x * 100).toFixed(2)}%/${(
                          items.pct_target_30x * 100
                        ).toFixed(2)}%`
                },
                {
                  key: 'Intron 20X coverage',
                  label: t('Intron 20X coverage'),
                  children: `${
                    items?.intron_pct_20x
                      ? `${items?.intron_pct_20x.toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'Mitochondrial 20x coverage',
                  label: t('Mitochondrial 20x coverage'),
                  children: `${
                    items?.mt_pct_20x || items?.mt_pct_20x === 0
                      ? `${(items?.mt_pct_20x * 100).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key:
                    items.vcfType === 'wgs'
                      ? 'Average sequencing depth (X)'
                      : 'Targeted average sequencing depth',
                  label:
                    items.vcfType === 'wgs'
                      ? t('Average sequencing depth (X)')
                      : t('Targeted average sequencing depth'),
                  children: `${
                    items?.mean_target_coverage
                      ? `${Number(items?.mean_target_coverage).toFixed(2)}X`
                      : ''
                  }`
                },
                {
                  key: 'Average Variant Depth',
                  label: t('Average Variant Depth'),

                  children: `${
                    items?.avgReadDepth
                      ? `${items?.avgReadDepth.toFixed(2)}X`
                      : ''
                  }`
                },
                {
                  key: 'Intron mean depth',
                  label: t('Intron mean depth'),
                  children: `${
                    items?.intron_mean_depth
                      ? `${items?.intron_mean_depth}X`
                      : ''
                  }`
                },
                {
                  key: 'Mean mitochondrial depth',
                  label: t('Mean mitochondrial depth'),
                  children: `${
                    items?.mt_mean_depth || items?.mt_mean_depth === 0
                      ? `${items?.mt_mean_depth}X`
                      : ''
                  }`
                },
                {
                  key: 'Total number of bases',
                  label: !TotalBasesQualified ? (
                    <Tooltip
                      title={`rule: ${
                        TotalBasesRule.op === 'gte' ? '>=' : '<='
                      } ${TotalBasesRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: TotalBasesQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('Total number of bases')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('Total number of bases')
                  ),
                  children: items?.total_bases
                    ? formatNumberWithCommas(items?.total_bases)
                    : ''
                },
                {
                  key: 'Raw reads',
                  label: !RawReadsQualified ? (
                    <Tooltip
                      title={`rule: ${
                        RawReadsRule.op === 'gte' ? '>=' : '<='
                      } ${RawReadsRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: RawReadsQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('Raw reads')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('Raw reads')
                  ),
                  children: `${
                    items?.total_reads
                      ? formatNumberWithCommas(items?.total_reads)
                      : ''
                  }`
                },
                {
                  key: 'aligned read count',
                  label: !AlignedReadsQualified ? (
                    <Tooltip
                      title={`rule: ${
                        AlignedReadsRule.op === 'gte' ? '>=' : '<='
                      } ${AlignedReadsRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: AlignedReadsQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('aligned read count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('aligned read count')
                  ),
                  children: `${
                    items?.reads_mapped
                      ? formatNumberWithCommas(items?.reads_mapped)
                      : ''
                  }`
                },
                {
                  key: 'aligned base count',
                  label: !AlignedBasesQualified ? (
                    <Tooltip
                      title={`rule: ${
                        AlignedBasesRule.op === 'gte' ? '>=' : '<='
                      } ${AlignedBasesRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: AlignedBasesQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('aligned bases count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('aligned bases count')
                  ),
                  children: `${
                    items?.bases_mapped
                      ? formatNumberWithCommas(items?.bases_mapped)
                      : ''
                  }`
                },
                {
                  key: 'read alignment ratio',
                  label: !ReadAlignmentRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        ReadAlignmentRatioRule.op === 'gte' ? '>=' : '<='
                      } ${ReadAlignmentRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: ReadAlignmentRatioQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('read alignment ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('read alignment ratio')
                  ),
                  children: `${
                    items?.reads_mapped
                      ? `${(
                          (items?.reads_mapped / items?.total_reads) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'base alignment ratio',
                  label: !BaseAlignmentRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        BaseAlignmentRatioRule.op === 'gte' ? '>=' : '<='
                      } ${BaseAlignmentRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: BaseAlignmentRatioQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('bases alignment ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('bases alignment ratio')
                  ),
                  children: `${
                    items?.bases_mapped
                      ? `${(
                          (items?.bases_mapped / items?.total_bases) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'filtered read count',
                  label: !FilteredReadsQualified ? (
                    <Tooltip
                      title={`rule: ${
                        FilteredReadsRule.op === 'gte' ? '>=' : '<='
                      } ${FilteredReadsRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: FilteredReadsQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('filtered read count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('filtered read count')
                  ),
                  children: `${
                    items?.total_reads_q20
                      ? formatNumberWithCommas(items?.total_reads_q20)
                      : ''
                  }`
                },
                {
                  key: 'filtered base count',
                  label: !FilteredBasesQualified ? (
                    <Tooltip
                      title={`rule: ${
                        FilteredBasesRule.op === 'gte' ? '>=' : '<='
                      } ${FilteredBasesRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: FilteredBasesQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('filtered bases count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('filtered bases count')
                  ),
                  children: `${
                    items?.total_bases_q20
                      ? formatNumberWithCommas(items?.total_bases_q20)
                      : ''
                  }`
                },
                {
                  key: 'read filtering retention ratio',
                  label: !ReadFilteringRetentionRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        ReadFilteringRetentionRatioRule.op === 'gte'
                          ? '>='
                          : '<='
                      } ${ReadFilteringRetentionRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: ReadFilteringRetentionRatioQualified
                            ? 'inherit'
                            : 'red'
                        }}
                      >
                        {t('read filtering retention ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('read filtering retention ratio')
                  ),
                  children: `${
                    items?.total_reads_q20
                      ? `${(
                          (items?.total_reads_q20 / items?.total_reads) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'base filtering retention ratio',
                  label: !BaseFilteringRetentionRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        BaseFilteringRetentionRatioRule.op === 'gte'
                          ? '>='
                          : '<='
                      } ${BaseFilteringRetentionRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: BaseFilteringRetentionRatioQualified
                            ? 'inherit'
                            : 'red'
                        }}
                      >
                        {t('bases filtering retention ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('bases filtering retention ratio')
                  ),
                  children: `${
                    items?.total_bases_q20
                      ? `${(
                          (items?.total_bases_q20 / items?.total_bases) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'targeted read count',
                  label: !TargetedReadsQualified ? (
                    <Tooltip
                      title={`rule: ${
                        TargetedReadsRule.op === 'gte' ? '>=' : '<='
                      } ${TargetedReadsRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: TargetedReadsQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('targeted read count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('targeted read count')
                  ),
                  children: `${
                    items?.reads_on_target
                      ? formatNumberWithCommas(items?.reads_on_target)
                      : ''
                  }`
                },
                {
                  key: 'targeted base count',
                  label: !TargetedBasesQualified ? (
                    <Tooltip
                      title={`rule: ${
                        TargetedBasesRule.op === 'gte' ? '>=' : '<='
                      } ${TargetedBasesRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: TargetedBasesQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('targeted bases count')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('targeted bases count')
                  ),
                  children: `${
                    items?.bases_on_target
                      ? formatNumberWithCommas(items?.bases_on_target)
                      : ''
                  }`
                },
                {
                  key: 'read targeting ratio',
                  label: !TargetedReadsRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        TargetedReadsRatioRule.op === 'gte' ? '>=' : '<='
                      } ${TargetedReadsRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: TargetedReadsRatioQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('read targeting ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('read targeting ratio')
                  ),
                  children: `${
                    items?.reads_on_target
                      ? `${(
                          (items?.reads_on_target / items?.total_reads) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'base targeting ratio',
                  label: !TargetedBasesRatioQualified ? (
                    <Tooltip
                      title={`rule: ${
                        TargetedBasesRatioRule.op === 'gte' ? '>=' : '<='
                      } ${TargetedBasesRatioRule.value}`}
                    >
                      <Typography
                        sx={{
                          color: TargetedBasesRatioQualified ? 'inherit' : 'red'
                        }}
                      >
                        {t('bases targeting ratio')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    t('bases targeting ratio')
                  ),
                  children: `${
                    items?.bases_on_target
                      ? `${(
                          (items?.bases_on_target / items?.total_bases) *
                          100
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'PCR duplicate',
                  label: t('PCR duplicate'),
                  children: `${(items?.pcr_duplicate_rate * 100).toFixed(2)}%`
                },

                // {
                //   key: 'Mean depth of target region',
                //   label: t('Mean depth of target region'),
                //   children: items?.fastqc?.metricsData?.targetMeanDepth
                // },
                {
                  key: 'Rate of nucleotide mismatch',
                  label: t('Rate of nucleotide mismatch'),
                  children: `${
                    items?.mismatch_rate
                      ? `${Number(items?.mismatch_rate * 100).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'TS/TV ratio',
                  label: t('TS/TV ratio'),
                  children: `${
                    items?.ts_tv_ratio
                      ? `${Number(items?.ts_tv_ratio).toFixed(2)}`
                      : ''
                  }`
                },
                {
                  key: 'Efficiency of targeted capture',
                  label: t('Efficiency of targeted capture'),
                  children: items?.capture_efficiency
                    ? `${Number(items?.capture_efficiency * 100).toFixed(2)}%`
                    : ''
                },
                {
                  key: 'Duplication rate',
                  label: t('Duplication rate'),
                  children: items?.pcr_duplicate_rate
                    ? `${Number(items?.pcr_duplicate_rate * 100).toFixed(2)}%`
                    : ''
                },
                {
                  key: 'Parentage',
                  label: t('Parentage'),
                  children: t(items?.type)
                },
                {
                  key: 'Sample contamination',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'column'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Tooltip
                        title={
                          <Box sx={{ maxWidth: 250 }}>
                            <Typography color={'white'} fontSize={14}>
                              {`< 0.01: ${t('No problem')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`0.01-0.03: ${t('Mild contamination')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`0.03–0.05: ${t('Moderate contamination')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`> 0.05: ${t('High contamination')}`}
                            </Typography>
                          </Box>
                        }
                        placement={'top-start'}
                      >
                        <Stack
                          spacing={4}
                          direction={'row'}
                          height={'100%'}
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              color: 'rgba(0, 0, 0, 0.65)',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            {t('Sample contamination')}
                          </Typography>

                          <IconButton>
                            <HelpCircleOutline />
                          </IconButton>
                        </Stack>
                      </Tooltip>

                      <Stack direction={'row'} alignItems="center">
                        <TextField
                          size="small"
                          placeholder={t(
                            'Customize the pollution ratio threshold'
                          )}
                          label={t('Customize the pollution ratio threshold')}
                          sx={{
                            maxWidth: 160
                          }}
                          value={ratio}
                          onChange={(e) => {
                            // if (Number(e.target.value) > 5) {
                            //   setRatio(5)
                            // } else if (Number(e.target.value) < 1) {
                            //   setRatio(1)
                            // } else {
                            //   setRatio(Number(e.target.value))
                            // }
                            setRatio(Number(e.target.value))
                          }}
                        />
                        %
                      </Stack>
                    </Stack>
                  ),
                  children:
                    items?.freemix && items?.freemix < 0.01 ? (
                      `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                        'No DNA contamination'
                      )}`
                    ) : items?.freemix > ratio / 100 ? (
                      <span
                        style={{
                          color: 'red'
                        }}
                      >{`(${items?.freemix})${t('DNA contamination')}`}</span>
                    ) : items?.freemix > 0.01 &&
                      items?.freemix < ratio / 100 ? (
                      <span
                        style={{
                          color: 'green'
                        }}
                      >{`(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                        'Possible DNA contamination'
                      )}`}</span>
                    ) : (
                      ''
                    ),
                  value:
                    items?.freemix && items?.freemix < 0.01
                      ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                          'No DNA contamination'
                        )}`
                      : items?.freemix > ratio / 100
                      ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                          'DNA contamination'
                        )}`
                      : items?.freemix > 0.01 && items?.freemix < ratio / 100
                      ? `(${`${(items?.freemix * 100).toFixed(2)}%`})${t(
                          'Possible DNA contamination'
                        )}`
                      : ''
                },
                {
                  key: 'fold-80',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'row'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Typography
                        sx={{
                          color: 'rgba(0, 0, 0, 0.65)',
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {t('fold-80')}
                      </Typography>

                      <IconButton onClick={handlePopoverOpen}>
                        <HelpCircleOutline />
                      </IconButton>
                    </Stack>
                  ),
                  children: `${
                    items?.fold_80_base_penalty
                      ? Number(items?.fold_80_base_penalty).toFixed(2)
                      : ''
                  }`
                },
                {
                  key: 'q30',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'row'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Typography
                        sx={{
                          color: 'rgba(0, 0, 0, 0.65)',
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {t('Q30')}
                      </Typography>
                    </Stack>
                  ),
                  children: (
                    <Box>
                      <DataGrid
                        rows={(items?.fastq_qc && Array.isArray(items?.fastq_qc)
                          ? // eslint-disable-next-line no-unsafe-optional-chaining
                            items?.fastq_qc
                          : []
                        ).map((item: any, index: number) => ({
                          ...item,
                          id: index
                        }))}
                        columns={q30Column}
                        disableColumnMenu
                        hideFooterPagination
                        sx={{
                          '& .MuiDataGrid-footerContainer': {
                            justifyContent: 'center',
                            display: 'none'
                          }
                        }}
                      />
                    </Box>
                  )
                }
              ]
            }
          } else {
            return {
              name: items.type,
              value: [
                {
                  key: 'Sample Id',
                  label: t('Sample Id'),
                  children: items.id
                },
                {
                  key: 'Total valid data volume',
                  label: t('Total valid data volume'),
                  children:
                    items.fileSize > 1000
                      ? `${(items.fileSize / 1024).toFixed(2)}G`
                      : `${items.fileSize}MB`
                },
                {
                  key: 'Target region length',
                  label: t('Target region length'),
                  children: items?.fastqc?.metricsData?.targetRegionLength
                },
                {
                  key:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? '1X/10X/20X/30X/40X/50X'
                      : '1x/4x/10x/20x/30x',
                  label:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? `${t('1X/10X/20X/30X/40X/50X')} ${t('Coverage')}`
                      : `${t('1x/4x/10x/20x/30x')} ${t('Coverage')}`,
                  children:
                    items.sampleType === 'fastq' &&
                    items.vcfType === 'wgs' &&
                    Array.isArray(items?.fastqc?.barChartData) ? (
                      `${items?.fastqc?.barChartData
                        ?.map((item: any) => `${item.value}%`)
                        .join('/')}`
                    ) : Array.isArray(
                        items?.fastqc?.metricsData?.targetRegionAverageDepths
                      ) ? (
                      <>
                        {`${
                          items?.fastqc?.metricsData?.targetRegionCoverage
                            ? `${items?.fastqc?.metricsData?.targetRegionCoverage}%`
                            : ''
                        }/${items?.fastqc?.metricsData?.targetRegionAverageDepths
                          .map((item: any) => `${item.value}%`)
                          .join('/')}`}
                      </>
                    ) : (
                      items?.fastqc?.metricsData?.targetRegionAverageDepths
                    ),
                  value:
                    items.sampleType === 'fastq' &&
                    items.vcfType === 'wgs' &&
                    Array.isArray(items?.fastqc?.barChartData)
                      ? `${items?.fastqc?.barChartData
                          ?.map((item: any) => `${item.value}%`)
                          .join('/')}`
                      : Array.isArray(
                          items?.fastqc?.metricsData?.targetRegionAverageDepths
                        )
                      ? `${
                          items?.fastqc?.metricsData?.targetRegionCoverage
                        }/${items?.fastqc?.metricsData?.targetRegionAverageDepths
                          .map((item: any) => `${item.value}%`)
                          .join('/')}`
                      : items?.fastqc?.metricsData?.targetRegionAverageDepths
                },
                {
                  key:
                    items.vcfType === 'wgs'
                      ? 'Average sequencing depth (X)'
                      : 'Targeted average sequencing depth',
                  label:
                    items.vcfType === 'wgs'
                      ? t('Average sequencing depth (X)')
                      : t('Targeted average sequencing depth'),
                  children:
                    items.vcfType === 'wgs'
                      ? `${
                          items?.fastqc?.metricsData?.meanCoverage
                            ? `${items?.fastqc?.metricsData?.meanCoverage}X`
                            : ''
                        }`
                      : `${
                          items?.fastqc?.metricsData?.targetMeanDepth
                            ? `${items?.fastqc?.metricsData?.targetMeanDepth}X`
                            : ''
                        }`
                },
                {
                  key: 'Average Variant Depth',
                  label: t('Average Variant Depth'),
                  children: `${
                    items?.avgReadDepth
                      ? `${items?.avgReadDepth.toFixed(2)}X`
                      : ''
                  }`
                },
                {
                  key: 'Intron mean depth',
                  label: t('Intron mean depth'),
                  children: `${
                    items?.fastqc?.metricsData?.intronMeanDepth
                      ? `${items?.fastqc?.metricsData?.intronMeanDepth}X`
                      : ''
                  }`
                },
                {
                  key: 'Intron 20X coverage',
                  label: t('Intron 20X coverage'),
                  children: `${
                    items?.fastqc?.metricsData?.intron20X
                      ? `${items?.fastqc?.metricsData?.intron20X}%`
                      : ''
                  }`
                },
                {
                  key: 'Mean mitochondrial depth',
                  label: t('Mean mitochondrial depth'),
                  children: `${
                    items?.fastqc?.metricsData?.mtMeanDepth
                      ? `${items?.fastqc?.metricsData?.mtMeanDepth}X`
                      : ''
                  }`
                },
                {
                  key: 'Mitochondrial 20x coverage',
                  label: t('Mitochondrial 20x coverage'),
                  children: `${
                    items?.fastqc?.metricsData?.mt20X
                      ? `${items?.fastqc?.metricsData?.mt20X}%`
                      : ''
                  }`
                },
                {
                  key: 'Total number of bases',
                  label: t('Total number of bases'),
                  children: items?.fastqc?.metricsData?.rawReads
                    ? `${
                        Math.round(
                          (Number(
                            `${calculateAndFormat(
                              `${items?.fastqc?.metricsData?.rawReads}`,
                              150
                            )}`.replace(/,/g, '')
                          ) /
                            1e9) *
                            100
                        ) / 100
                      }G`
                    : ''
                },
                {
                  key: 'Raw reads',
                  label: t('Raw reads'),
                  children: `${
                    items?.fastqc?.metricsData?.rawReads
                      ? items?.fastqc?.metricsData?.rawReads
                      : ''
                  }`
                },
                {
                  key: 'Reads mapped to genome',
                  label: t('Reads mapped to genome'),
                  children: `${
                    items?.fastqc?.metricsData?.readsMapped
                      ? items?.fastqc?.metricsData?.readsMapped
                      : ''
                  }`
                },
                {
                  key: 'mappingrate',
                  label: t('mappingrate'),
                  children:
                    items?.fastqc?.metricsData?.rawReads &&
                    items?.fastqc?.metricsData?.readsMapped
                      ? `${
                          Number(
                            (
                              items?.fastqc?.metricsData?.readsMapped
                                .split(',')
                                .join('') /
                              items?.fastqc?.metricsData?.rawReads
                                .split(',')
                                .join('')
                            ).toFixed(4)
                          ) * 100
                        }%`
                      : ''
                },
                {
                  key: 'Reads mapped target region',
                  label: t('Reads mapped target region'),
                  children:
                    items.sampleType === 'fastq' && items.vcfType === 'wgs'
                      ? items?.fastqc?.metricsData?.readsMapped
                      : items?.fastqc?.metricsData?.readsMappedOnTargetRegion
                },
                {
                  key: 'PCR duplicate',
                  label: t('PCR duplicate'),
                  children: items?.fastqc?.metricsData?.pcrDuplicateRate
                },

                // {
                //   key: 'Mean depth of target region',
                //   label: t('Mean depth of target region'),
                //   children: items?.fastqc?.metricsData?.targetMeanDepth
                // },
                {
                  key: 'Rate of nucleotide mismatch',
                  label: t('Rate of nucleotide mismatch'),
                  children: `${
                    items?.fastqc?.metricsData?.mismatchRate
                      ? `${Number(
                          items?.fastqc?.metricsData?.mismatchRate
                        ).toFixed(2)}%`
                      : ''
                  }`
                },
                {
                  key: 'TS/TV ratio',
                  label: t('TS/TV ratio'),
                  children: `${
                    items?.tstv?.ratio
                      ? `${Number(items?.tstv?.ratio).toFixed(2)}`
                      : ''
                  }`
                },
                {
                  key: 'Efficiency of targeted capture',
                  label: t('Efficiency of targeted capture'),
                  children:
                    items?.fastqc?.metricsData?.readsOnTargetRegionsPercent
                },
                {
                  key: 'Duplication rate',
                  label: t('Duplication rate'),
                  children: items?.fastqc?.metricsData?.readsAfterDedupedPercent
                },
                {
                  key: 'Sample contamination',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'column'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Tooltip
                        title={
                          <Box sx={{ maxWidth: 250 }}>
                            <Typography color={'white'} fontSize={14}>
                              {`< 0.01: ${t('No problem')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`0.01-0.03: ${t('Mild contamination')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`0.03–0.05: ${t('Moderate contamination')}`}
                            </Typography>

                            <Typography color={'white'} fontSize={14}>
                              {`> 0.05: ${t('High contamination')}`}
                            </Typography>
                          </Box>
                        }
                        placement={'top-start'}
                      >
                        <Stack
                          spacing={4}
                          direction={'row'}
                          height={'100%'}
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              color: 'rgba(0, 0, 0, 0.65)',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            {t('Sample contamination')}
                          </Typography>

                          <IconButton>
                            <HelpCircleOutline />
                          </IconButton>
                        </Stack>
                      </Tooltip>

                      <Stack direction={'row'} alignItems="center">
                        <TextField
                          size="small"
                          placeholder={t(
                            'Customize the pollution ratio threshold'
                          )}
                          label={t('Customize the pollution ratio threshold')}
                          sx={{
                            maxWidth: 160
                          }}
                          value={ratio}
                          onChange={(e) => {
                            // if (Number(e.target.value) > 5) {
                            //   setRatio(5)
                            // } else if (Number(e.target.value) < 1) {
                            //   setRatio(1)
                            // } else {
                            //   setRatio(Number(e.target.value))
                            // }
                            setRatio(Number(e.target.value))
                          }}
                        />
                        %
                      </Stack>
                    </Stack>
                  ),
                  children:
                    items?.freemixScore && items?.freemixScore < 0.01 ? (
                      `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                        'No DNA contamination'
                      )}`
                    ) : items?.freemixScore > ratio / 100 ? (
                      <span
                        style={{
                          color: 'red'
                        }}
                      >{`(${items?.freemixScore})${t(
                        'DNA contamination'
                      )}`}</span>
                    ) : items?.freemixScore > 0.01 &&
                      items?.freemixScore < ratio / 100 ? (
                      <span
                        style={{
                          color: 'green'
                        }}
                      >{`(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                        'Possible DNA contamination'
                      )}`}</span>
                    ) : (
                      ''
                    ),
                  value:
                    items?.freemixScore && items?.freemixScore < 0.01
                      ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                          'No DNA contamination'
                        )}`
                      : items?.freemixScore > ratio / 100
                      ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                          'DNA contamination'
                        )}`
                      : items?.freemixScore > 0.01 &&
                        items?.freemixScore < ratio / 100
                      ? `(${`${(items?.freemixScore * 100).toFixed(2)}%`})${t(
                          'Possible DNA contamination'
                        )}`
                      : ''
                },
                {
                  key: 'fold-80',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'row'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Typography
                        sx={{
                          color: 'rgba(0, 0, 0, 0.65)',
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {t('fold-80')}
                      </Typography>

                      <IconButton onClick={handlePopoverOpen}>
                        <HelpCircleOutline />
                      </IconButton>
                    </Stack>
                  ),
                  children: `${items?.fold80 ? items?.fold80 : ''}`
                },
                {
                  key: 'q30',
                  label: (
                    <Stack
                      spacing={4}
                      direction={'row'}
                      height={'100%'}
                      alignItems="center"
                      justifyContent={'center'}
                    >
                      <Typography
                        sx={{
                          color: 'rgba(0, 0, 0, 0.65)',
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {t('Q30')}
                      </Typography>
                    </Stack>
                  ),
                  children: (
                    <Box>
                      <DataGrid
                        rows={(items?.fastqc?.metricsData?.seqtk_q30 &&
                        Array.isArray(items?.fastqc?.metricsData?.seqtk_q30)
                          ? // eslint-disable-next-line no-unsafe-optional-chaining
                            items?.fastqc?.metricsData?.seqtk_q30
                          : []
                        ).map((item: any, index: number) => ({
                          ...item,
                          id: index
                        }))}
                        columns={q30Column}
                        disableColumnMenu
                        hideFooterPagination
                        sx={{
                          '& .MuiDataGrid-footerContainer': {
                            justifyContent: 'center',
                            display: 'none'
                          }
                        }}
                      />
                    </Box>
                  )
                },
                {
                  key: 'Parentage',
                  label: t('Parentage'),
                  children: t(items?.type)
                }
              ]
            }
          }
        })
    }, [samples, ratio, i18n.language, projects])

    const Item = (item: any, key: string) => {
      return (
        <Descriptions
          extra={
            <IconButton
              onClick={() => {
                //
                const column = item.item.map((i: any) => {
                  if (i.key === 'fold-80')
                    return {
                      title: 'fold-80',
                      key: 'fold-80'
                    }
                  else
                    return {
                      title: i.label,
                      key: i.label
                    }
                })

                const data = items.map((item: any) => {
                  const obj: NObject = {}

                  if (Array.isArray(item)) {
                    item.map((i: any) => {
                      if (i.key === 'fold-80')
                        obj['fold-80'] = i.children
                          ? JSON.stringify(i.children)
                          : 'null'
                      else if (i.key === '1x/4x/10x/20x/30x')
                        obj['1x/4x/10x/20x/30x'] = i.value
                      else if (i.key === '1X/10X/20X/30X/40X/50X')
                        obj['1X/10X/20X/30X/40X/50X'] = i.value
                      else if (i.key === 'Sample contamination')
                        obj[t('Sample contamination')] = i.value
                      else
                        obj[i.label] = i.children
                          ? JSON.stringify(i.children)
                          : 'null'
                    })
                  } else {
                    item.value.map((i: any) => {
                      if (i.key === 'fold-80')
                        obj['fold-80'] = i.children
                          ? JSON.stringify(i.children)
                          : 'null'
                      else if (i.key === '1x/4x/10x/20x/30x')
                        obj['1x/4x/10x/20x/30x'] = i.value
                      else if (i.key === '1X/10X/20X/30X/40X/50X')
                        obj['1X/10X/20X/30X/40X/50X'] = i.value
                      else if (i.key === 'Sample contamination')
                        obj[t('Sample contamination')] = i.value
                      else
                        obj[i.label] = i.children
                          ? JSON.stringify(i.children)
                          : 'null'
                    })
                  }

                  return obj
                })

                const arr = data.map((item) => {
                  const obj: NObject = {}

                  column.map((i: any) => {
                    obj[i.title] = item[i.key]
                  })

                  return obj
                })

                // 导出XLSX
                const worksheet = XLSX.utils.json_to_sheet(arr)
                const workbook = XLSX.utils.book_new()

                XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

                XLSX.writeFile(
                  workbook,
                  `${props.analysisInfo.name}-${formatDate(new Date())}.xlsx`
                )
              }}
            >
              <OpenInNew />
            </IconButton>
          }
          title={t('Quality control')}
          layout="vertical"
          bordered
          items={item.item}
          key={item.keys}
          column={isWindow() ? 6 : 2}
          labelStyle={{
            display: 'inline-block',
            width: '100%',
            height: '100%',
            padding: '16px 12px',
            textAlign: 'center'
          }}
          contentStyle={{
            display: 'inline-block',
            width: '100%',
            height: '100%',
            textAlign: 'center'
          }}
        />
      )
    }

    const QualityTab = () => {
      if (items.length === 1) {
        const item = items[0]

        return <Item item={item} />
      } else {
        return (
          <Box>
            <Tabs
              value={tabValue}
              onChange={(e: any, newValue) => {
                setTabValue(newValue)
              }}
              aria-label="basic tabs example"
            >
              {items.map((item, index) => {
                return (
                  <Tab
                    key={`${item.name}_tab`}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                    label={t(item.name ?? '')}
                    {...a11yProps(index)}
                  />
                )
              })}
            </Tabs>

            {items.map((item, index) => {
              return (
                <TabPanel
                  key={`${item.name}_panel`}
                  value={tabValue}
                  index={index}
                >
                  <Item item={item.value} key={item.name} keys={item.name} />
                </TabPanel>
              )
            })}
          </Box>
        )
      }
    }

    React.useImperativeHandle(ref, () => {
      return {}
    })

    return (
      <Box id="print" overflow="auto">
        <Grid container height={'100%'} spacing={5}>
          <Grid item xs={12}>
            <Stack direction={'column'} spacing={5}>
              <QualityTab />
            </Stack>
          </Grid>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            sx={{
              '& .MuiPaper-root': {
                width: '700px',
                height: '280px',
                right: 0,
                left: 0,
                margin: '0 auto'
              }
            }}
          >
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fold-80 Value</TableCell>

                      <TableCell align="right">Coverage Uniformity</TableCell>

                      <TableCell align="right">Interpretation</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.value}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.value}
                        </TableCell>

                        <TableCell align="right">
                          {row.CoverageUniformity}
                        </TableCell>

                        <TableCell align="right">
                          {row.Interpretation}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Popover>

          {samples.length >= 2 ? (
            <>
              <Grid item xs={12} md={1.8}>
                <Card sx={{ height: '100%' }}>
                  <CardContent
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      overflow: 'auto',
                      minHeight: 280
                    }}
                  >
                    <Tooltip
                      title={t(
                        'You can view it by sliding the options to check the allele frequency range and view the corresponding statistics.'
                      )}
                    >
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              defaultChecked
                              onChange={(e) => {
                                setSlider(!e.target.checked)

                                // 过滤数据
                              }}
                            />
                          }
                          label={t('Allele Frequency')}
                          sx={{
                            '& .MuiTypography-root': {
                              fontWeight: 500,
                              fontSize: 16
                            }
                          }}
                        />
                      </FormGroup>
                    </Tooltip>

                    <Slider
                      getAriaLabel={() => 'Temperature'}
                      orientation="vertical"
                      getAriaValueText={valuetext}
                      value={value}
                      valueLabelDisplay="auto"
                      sx={{ maxHeight: '170px' }}
                      marks={marks}
                      onChange={handleChangeValue}
                      disabled={slider}
                      onChangeCommitted={async (e, v) => {
                        if (v && Array.isArray(v) && v.length) {
                          const params = {
                            start: v[0],
                            end: v[1],
                            id: id
                          }
                          const AlleleFrequencyQC =
                            await fetchAlleleFrequencyQC.mutateAsync(params)
                          const ReadDepth = await fetchReadDepth.mutateAsync(
                            params
                          )

                          //
                        }
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5.2}>
                <Card>
                  <CardContent>
                    <Tooltip
                      title={t(
                        'The number of reads aligned to the reference position. The higher the read depth, the higher the confidence in the positive variations in the sample.'
                      )}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: 16
                        }}
                        textAlign="center"
                        textTransform={'capitalize'}
                      >
                        {t('Variation depth')}
                      </Typography>
                    </Tooltip>

                    <Histograms ref={his1} {...hisConfig1} data={hisData} />

                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: 16
                      }}
                      textAlign="center"
                    >
                      {t('Reads depth')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%' }}>
                  <CardContent
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Grid container spacing={6}>
                      <Grid item xs={12} md={4}>
                        <Tooltip
                          title={t(
                            'The total number of genomic and exon variants (encoding protein variants) of the sample'
                          )}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: 16
                            }}
                          >
                            {t('Genomic Variants')}
                          </Typography>
                        </Tooltip>

                        <Typography fontSize={48} marginTop={4}>
                          {qc.genomicCount}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Exon  Variants')}
                        </Typography>

                        <Typography fontSize={48} marginTop={4}>
                          {qc.exonicCount}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Intron Variants')}
                        </Typography>

                        <Typography fontSize={48} marginTop={4}>
                          {Number(qc.intronicCount)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Genomic avg read depth')}
                        </Typography>

                        <Typography fontSize={48} marginTop={4}>
                          {qc.avgReadDepth}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Exon avg read depth')}
                        </Typography>

                        <Typography fontSize={48} marginTop={4}>
                          {qc.avgExonReadDepth}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Intron avg read depth')}
                        </Typography>

                        <Typography fontSize={48} marginTop={4}>
                          {qc.avgIntronReadDepth}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            ''
          )}

          {props.analysisInfo.sampleType === 'fastq' ||
          props.analysisInfo.sampleType === 'naf' ? (
            <>
              {/* <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ height: '100%' }}>
                    <Grid container height={'100%'} spacing={6}>
                      <Grid item xs={12} md={7}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Raw Reads')}
                        </Typography>

                        <Typography fontSize={32}>
                          {fastqc?.metricsData?.rawReads ?? null}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Reads Mapped')}
                        </Typography>

                        <Typography fontSize={32}>
                          {fastqc?.metricsData?.readsMapped}
                        </Typography>

                        <Typography fontSize={16} textAlign="center">
                          {fastqc?.metricsData?.readsMappedPercent}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={7} mt={5}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {t('Duplicates Rate')}
                        </Typography>

                        <Typography fontSize={32}>
                          {fastqc?.metricsData?.readsMapped}
                        </Typography>

                        <Typography fontSize={16} pl={20} textAlign="left">
                          {fastqc?.metricsData?.readsAfterDedupedPercent}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={5} mt={5}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 16
                          }}
                        >
                          {props?.analysisInfo?.vcfType === 'wgs'
                            ? t('Mean Coverage')
                            : t('On Target')}
                        </Typography>

                        <React.Fragment>
                          <Typography fontSize={32}>
                            {props?.analysisInfo?.vcfType === 'wgs'
                              ? fastqc?.metricsData?.meanCoveragePercent
                              : fastqc?.metricsData
                                  ?.readsOnTargetRegionsPercent}
                          </Typography>

                          <Typography fontSize={16} textAlign="left">
                            {fastqc?.metricsData?.SDCoverage}
                          </Typography>
                        </React.Fragment>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid> */}

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ height: '100%' }}>
                    <Tooltip
                      title={t('The sequencing depth of the target base')}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: 16
                        }}
                        textAlign="center"
                        textTransform={'capitalize'}
                      >
                        {t('Minimum Coverage')}
                      </Typography>
                    </Tooltip>

                    <HistogramFq ref={his2} {...hisConfig2} data={hisData2} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                <Card sx={{ width: '100%', height: '100%' }}>
                  <CardContent sx={{ width: '100%', height: '100%' }}>
                    <Tooltip
                      title={t(
                        'Scatter plot of sequencing depth for different chromosomes'
                      )}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: 16
                        }}
                        textAlign="center"
                        textTransform={'capitalize'}
                      >
                        {t('Average Reads Depth')}
                      </Typography>
                    </Tooltip>

                    {tsvData && tsvData.length ? (
                      <Scatters
                        ref={scatter}
                        value={tsvData}
                        key={tsvData.length}
                      />
                    ) : (
                      ''
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            ''
          )}
        </Grid>

        {props.isSample ? (
          <Box>
            <iframe
              width={'100%'}
              style={{
                minHeight: '924px'
              }}
              src={
                props.analysisInfo.pipelineVersion && GlsQc?.vcf_path
                  ? `/vcf.iobio.io/index.html?furl=${GlsQc.vcf_path}&qcurl=${process.env.QC_URL}&is_ssl=${process.env.QC_IS_SSL}&system=${process.env.SYSTEM}`
                  : `/vcf.iobio.io/index.html?furl=/glsofort/samples/${props.analysisInfo.id}/vcf_results/${props.analysisInfo.prefix}.vcf_tsv.af.vcf.gz&qcurl=${process.env.QC_URL}&is_ssl=${process.env.QC_IS_SSL}&system=${process.env.SYSTEM}`
              }
            />
          </Box>
        ) : (
          ''
        )}

        <Box>
          {props.analysisInfo.hasRoh && props.isSample ? (
            <Grid
              container
              sx={{
                p: 1,
                border: '1px solid rgb(230,230,230)',
                borderRadius: '2pt',
                bgcolor: 'white'
              }}
            >
              <Grid item xs={12} md={8}>
                <Box textAlign={'center'}>
                  <Tooltip
                    title={t('Conduct a screening of close relatives for UPD.')}
                  >
                    <>
                      <Typography>{t('Run of Homozygous Regions')}</Typography>

                      <Typography>
                        {`Total ${megabase.totalMegabase} (`}

                        <span
                          style={{
                            color: 'red'
                          }}
                        >
                          {` IBD `}
                        </span>

                        <span>{`${megabase.megabasePercentage} of Genome)`}</span>
                      </Typography>
                    </>
                  </Tooltip>

                  <img
                    style={{
                      verticalAlign: 'middle',
                      border: 0
                    }}
                    src={megabase.imageUrl}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : (
            ''
          )}
        </Box>

        <Box>
          {(props.analysisInfo.sampleType === 'fastq' ||
            props.analysisInfo.sampleType === 'naf') &&
          props.isSample ? (
            <QualityControlFq2
              analysisInfo={props.analysisInfo}
              plotUrls={fastqc.metricsReports}
            />
          ) : (
            ''
          )}
        </Box>
      </Box>
    )
  }
)
export default QualityControl
